// src/services/indexedDBStore.js
// Low-level IndexedDB wrapper for persistent admin caching
// Isolates all IndexedDB complexity so cacheService stays clean

const DB_NAME = 'cm_admin_cache';
const DB_VERSION = 1;
const STORE_NAME = 'cache_entries';

let dbInstance = null;
let dbPromise = null;

/**
 * Check if IndexedDB is available in the current browser context
 */
function isIndexedDBAvailable() {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch (e) {
    return false;
  }
}

/**
 * Open or return the existing IndexedDB connection
 */
function openDB() {
  if (dbInstance) return Promise.resolve(dbInstance);
  if (dbPromise) return dbPromise;

  if (!isIndexedDBAvailable()) {
    return Promise.resolve(null);
  }

  dbPromise = new Promise((resolve, reject) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME);
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        dbInstance = event.target.result;

        // Handle connection closing unexpectedly
        dbInstance.onclose = () => {
          dbInstance = null;
          dbPromise = null;
        };

        dbPromise = null;
        resolve(dbInstance);
      };

      request.onerror = (event) => {
        console.warn('[IndexedDB] Failed to open database:', event.target.error);
        dbPromise = null;
        resolve(null);
      };

      request.onblocked = () => {
        console.warn('[IndexedDB] Database open blocked');
        dbPromise = null;
        resolve(null);
      };
    } catch (e) {
      console.warn('[IndexedDB] Exception opening database:', e.message);
      dbPromise = null;
      resolve(null);
    }
  });

  return dbPromise;
}

/**
 * Get a cache entry by key
 * @param {string} key - Cache key
 * @returns {Promise<Object|null>} Cache entry or null
 */
async function idbGet(key) {
  try {
    const db = await openDB();
    if (!db) return null;

    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => {
        console.warn('[IndexedDB] Get failed for key:', key);
        resolve(null);
      };
    });
  } catch (e) {
    console.warn('[IndexedDB] Get error:', e.message);
    return null;
  }
}

/**
 * Set a cache entry
 * @param {string} key - Cache key
 * @param {Object} entry - Cache entry { data, expiresAt, type, params, cachedAt }
 * @returns {Promise<boolean>} Success
 */
async function idbSet(key, entry) {
  try {
    const db = await openDB();
    if (!db) return false;

    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(entry, key);

      request.onsuccess = () => resolve(true);
      request.onerror = () => {
        console.warn('[IndexedDB] Set failed for key:', key);
        resolve(false);
      };
    });
  } catch (e) {
    console.warn('[IndexedDB] Set error:', e.message);
    return false;
  }
}

/**
 * Delete a single cache entry
 * @param {string} key - Cache key
 * @returns {Promise<boolean>} Success
 */
async function idbDelete(key) {
  try {
    const db = await openDB();
    if (!db) return false;

    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => resolve(true);
      request.onerror = () => {
        console.warn('[IndexedDB] Delete failed for key:', key);
        resolve(false);
      };
    });
  } catch (e) {
    console.warn('[IndexedDB] Delete error:', e.message);
    return false;
  }
}

/**
 * Delete all entries whose key starts with a given prefix
 * @param {string} prefix - Key prefix to match
 * @returns {Promise<boolean>} Success
 */
async function idbDeleteByPrefix(prefix) {
  try {
    const db = await openDB();
    if (!db) return false;

    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.openCursor();
      let deleted = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (typeof cursor.key === 'string' && cursor.key.startsWith(prefix)) {
            cursor.delete();
            deleted++;
          }
          cursor.continue();
        } else {
          if (deleted > 0) {
            console.log(`[IndexedDB] Deleted ${deleted} entries with prefix: ${prefix}`);
          }
          resolve(true);
        }
      };

      request.onerror = () => {
        console.warn('[IndexedDB] DeleteByPrefix failed for:', prefix);
        resolve(false);
      };
    });
  } catch (e) {
    console.warn('[IndexedDB] DeleteByPrefix error:', e.message);
    return false;
  }
}

/**
 * Clear all entries in the store
 * @returns {Promise<boolean>} Success
 */
async function idbClear() {
  try {
    const db = await openDB();
    if (!db) return false;

    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[IndexedDB] All entries cleared');
        resolve(true);
      };
      request.onerror = () => {
        console.warn('[IndexedDB] Clear failed');
        resolve(false);
      };
    });
  } catch (e) {
    console.warn('[IndexedDB] Clear error:', e.message);
    return false;
  }
}

/**
 * Delete all expired entries using the expiresAt index
 * @returns {Promise<number>} Number of entries cleaned up
 */
async function idbCleanupExpired() {
  try {
    const db = await openDB();
    if (!db) return 0;

    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('expiresAt');
      const range = IDBKeyRange.upperBound(Date.now());
      const request = index.openCursor(range);
      let cleaned = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cleaned++;
          cursor.continue();
        } else {
          if (cleaned > 0) {
            console.log(`[IndexedDB] Cleaned up ${cleaned} expired entries`);
          }
          resolve(cleaned);
        }
      };

      request.onerror = () => {
        console.warn('[IndexedDB] Cleanup failed');
        resolve(0);
      };
    });
  } catch (e) {
    console.warn('[IndexedDB] Cleanup error:', e.message);
    return 0;
  }
}

export {
  idbGet,
  idbSet,
  idbDelete,
  idbDeleteByPrefix,
  idbClear,
  idbCleanupExpired,
  isIndexedDBAvailable,
};
